import React, { useEffect, useMemo, useRef, useState } from "react";

const MENU_MARGIN = 8;
const MIN_MENU_WIDTH = 280;

const readOptions = (select) => Array.from(select.options)
  .filter((option) => !option.hidden)
  .map((option, index) => ({
    index,
    value: option.value,
    label: option.textContent?.trim() || option.label || option.value,
    disabled: option.disabled || option.parentElement?.disabled || false,
    group: option.parentElement?.tagName === "OPTGROUP"
      ? option.parentElement.label
      : ""
  }));

const calculatePosition = (select) => {
  const rect = select.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(
    Math.max(rect.width, MIN_MENU_WIDTH),
    viewportWidth - (MENU_MARGIN * 2)
  );
  const left = Math.min(
    Math.max(rect.left, MENU_MARGIN),
    viewportWidth - width - MENU_MARGIN
  );
  const spaceBelow = viewportHeight - rect.bottom - MENU_MARGIN;
  const spaceAbove = rect.top - MENU_MARGIN;

  if (spaceBelow < 220 && spaceAbove > spaceBelow) {
    return {
      left,
      width,
      bottom: viewportHeight - rect.top + 4,
      maxHeight: Math.max(150, Math.min(360, spaceAbove - 4))
    };
  }

  return {
    left,
    width,
    top: rect.bottom + 4,
    maxHeight: Math.max(150, Math.min(360, spaceBelow - 4))
  };
};

const setNativeSelectValue = (select, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLSelectElement.prototype,
    "value"
  )?.set;

  if (valueSetter) valueSetter.call(select, value);
  else select.value = value;

  select.dispatchEvent(new Event("input", { bubbles: true }));
  select.dispatchEvent(new Event("change", { bubbles: true }));
};

const GlobalSearchableSelect = () => {
  const [picker, setPicker] = useState(null);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  const closePicker = () => {
    setPicker(null);
    setQuery("");
  };

  const openPicker = (select, initialQuery = "") => {
    if (!select || select.disabled || select.multiple || select.size > 1) return;
    setPicker({
      select,
      options: readOptions(select),
      selectedValue: select.value,
      position: calculatePosition(select)
    });
    setQuery(initialQuery);
  };

  useEffect(() => {
    if (picker) searchRef.current?.focus();
  }, [picker]);

  useEffect(() => {
    const handleMouseDown = (event) => {
      const select = event.target.closest?.("select");
      if (select) {
        if (select.disabled || select.multiple || select.size > 1) return;
        event.preventDefault();
        openPicker(select);
        return;
      }

      if (!event.target.closest?.(".global-searchable-select-menu")) {
        closePicker();
      }
    };

    const handleClick = (event) => {
      const select = event.target.closest?.("select");
      if (!select || select.disabled || select.multiple || select.size > 1) return;
      event.preventDefault();
      openPicker(select);
    };

    const handleKeyDown = (event) => {
      const select = event.target.closest?.("select");
      if (!select || select.disabled || select.multiple || select.size > 1) return;
      const opensPicker = ["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key);
      const startsSearch = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
      if (!opensPicker && !startsSearch) return;

      event.preventDefault();
      openPicker(select, startsSearch ? event.key : "");
    };

    const closeOnViewportChange = () => closePicker();

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("resize", closeOnViewportChange);
    window.addEventListener("scroll", closeOnViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("resize", closeOnViewportChange);
      window.removeEventListener("scroll", closeOnViewportChange, true);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    if (!picker) return [];
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return picker.options;

    return picker.options.filter((option) => (
      option.value !== "" &&
      `${option.label} ${option.value}`.toLocaleLowerCase().includes(normalizedQuery)
    ));
  }, [picker, query]);

  const chooseOption = (option) => {
    if (!picker || option.disabled || !picker.select.isConnected) return;
    setNativeSelectValue(picker.select, option.value);
    picker.select.focus();
    closePicker();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      picker?.select.focus();
      closePicker();
      return;
    }

    if (event.key === "Enter") {
      const firstEnabledOption = filteredOptions.find((option) => !option.disabled);
      if (firstEnabledOption) {
        event.preventDefault();
        chooseOption(firstEnabledOption);
      }
    }
  };

  if (!picker) return null;

  return (
    <div
      className="global-searchable-select-menu"
      style={picker.position}
      role="dialog"
      aria-label="Search dropdown options"
    >
      <div className="global-searchable-select-search-wrap">
        <input
          ref={searchRef}
          type="search"
          className="form-control form-control-sm global-searchable-select-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Type to search..."
          aria-label="Search options"
          autoComplete="off"
          data-no-uppercase
        />
      </div>
      <div className="global-searchable-select-options" role="listbox">
        {filteredOptions.length === 0 ? (
          <div className="global-searchable-select-empty">No matching records</div>
        ) : filteredOptions.map((option) => (
          <button
            key={`${option.index}-${option.value}`}
            type="button"
            className={`global-searchable-select-option${option.value === picker.selectedValue ? " selected" : ""}`}
            disabled={option.disabled}
            role="option"
            aria-selected={option.value === picker.selectedValue}
            onMouseDown={(event) => {
              event.preventDefault();
              chooseOption(option);
            }}
          >
            <span>{option.label}</span>
            {option.group && <small>{option.group}</small>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default GlobalSearchableSelect;
